/**
 * iter-154r — Reactivate page.
 *
 * Lands here when an API call returned 402 (subscription canceled).
 * Surfaces a clear explanation + a one-click "Reactivate via Stripe"
 * button that opens the Stripe customer portal. Sign-out is also
 * available so the user can switch accounts.
 *
 * This page intentionally is NOT inside the ProtectedRoute layout —
 * the layout's API calls would all 402-redirect-loop here. We mount
 * it standalone with its own minimal chrome and rely on the user's
 * existing cookies + the billing-routes' relaxed auth.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CreditCard, LogOut, Loader2, AlertTriangle, LifeBuoy } from 'lucide-react';
import { safeExternalRedirect } from '@/lib/safeUrl';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingReactivatePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/billing/status');
        setStatus(data);
        // If billing is actually fine (e.g. user navigated here manually),
        // bounce them to the dashboard — UNLESS a platform admin has
        // manually locked the workspace (access_locked), in which case
        // they must stay here and contact support.
        if (
          (data.billing_status === 'active' || data.billing_status === 'trialing') &&
          !data.access_locked
        ) {
          navigate('/', { replace: true });
        }
      } catch (e) {
        // 401 → not logged in. Send to /login.
        if (e.response?.status === 401) {
          navigate('/login', { replace: true });
        } else {
          toast.error(e.response?.data?.detail || 'Could not fetch billing status');
        }
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, [navigate]);

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await apiClient.post('/billing/portal-session', { return_url: window.location.origin + '/' });
      if (data?.url) {
        if (!safeExternalRedirect(data.url)) {
          toast.error('Received an unexpected billing URL. Please try again.');
          setOpeningPortal(false);
        }
      } else {
        toast.error('No portal URL returned');
        setOpeningPortal(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open the billing portal');
      setOpeningPortal(false);
    }
  };

  const signOut = async () => {
    try { await apiClient.post('/auth/logout', {}); } catch (_) {}
    window.location.href = '/login';
  };

  if (loadingStatus) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isLocked = !!status?.access_locked;
  const isCanceled = status?.billing_status === 'canceled';
  const isPastDue = status?.billing_status === 'past_due';

  return (
    <div data-testid="billing-reactivate-page" className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <Card className="w-full max-w-lg bg-card border-border p-8">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold leading-tight">
              {isLocked
                ? 'Workspace locked'
                : isCanceled
                  ? 'Subscription canceled'
                  : isPastDue
                    ? 'Access paused — payment failed'
                    : 'Billing attention needed'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isLocked
                ? 'This workspace has been locked. Please contact support to restore access — your workspace and data are safe.'
                : isCanceled
                  ? "Your Strata subscription has ended. Your workspace data is still safe, but you'll need to reactivate to use the app again."
                  : isPastDue
                    ? "We couldn't process your latest invoice, and the grace period has ended, so access is paused. Please contact support to restore access — your workspace and data are safe."
                    : `Current status: ${status?.billing_status}. Resolve it via the Stripe portal to restore access.`}
            </p>
          </div>
        </div>

        <div className="bg-background border border-border rounded p-4 text-xs text-muted-foreground mb-6 space-y-1">
          <p><span className="text-muted-foreground">Plan tier:</span> <strong className="text-foreground">{status?.plan_revenue_tier_m || '—'}</strong></p>
          <p><span className="text-muted-foreground">Billing cycle:</span> <strong className="text-foreground">{status?.billing_cadence || '—'}</strong></p>
          <p><span className="text-muted-foreground">Subscription status:</span> <strong className="text-foreground">{status?.subscription_status || '—'}</strong></p>
        </div>

        <div className="flex items-center gap-3">
          {!isLocked && (
            <Button
              onClick={openPortal}
              disabled={openingPortal || !status?.has_stripe_customer}
              data-testid="reactivate-portal-btn"
              className="flex-1 bg-[#5C8AC2] hover:bg-[#7AA3D4] text-white"
            >
              {openingPortal ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening portal…</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> {isCanceled ? 'Reactivate subscription' : isPastDue ? 'Update payment method' : 'Manage billing'}</>
              )}
            </Button>
          )}
          <Button
            onClick={() => { window.location.href = 'mailto:support@strataxframework.com'; }}
            data-testid="reactivate-contact-support-btn"
            variant={isLocked ? 'default' : 'outline'}
            className={isLocked
              ? 'flex-1 bg-[#5C8AC2] hover:bg-[#7AA3D4] text-white'
              : 'bg-transparent border-border hover:bg-[#1F1F22] text-foreground'}
          >
            <LifeBuoy className="w-4 h-4 mr-2" />
            Contact support
          </Button>
          <Button
            onClick={signOut}
            data-testid="reactivate-signout-btn"
            variant="outline"
            className="bg-transparent border-border hover:bg-[#1F1F22] text-foreground"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>

        {!isLocked && !status?.has_stripe_customer && (
          <p className="mt-4 text-xs text-amber-300/80">
            No Stripe customer is linked to this workspace. Contact{' '}
            <a href="mailto:support@strataxframework.com" className="underline">support</a> to reactivate.
          </p>
        )}
      </Card>
    </div>
  );
}
